import { FaPaperclip } from "react-icons/fa";
import { useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/layout";
import OutlineButton from "ui/components/Button/OutlineButton";
import Button from "ui/components/Button";
import colors from "core/resources/theme/colors";
import { AiOutlineFile } from "react-icons/ai";
import { showError } from "infra/helpers/alerta";

export interface ButtonAnexarArquivosProps {
  onAnexar: (files: File[]) => any;
  variant?: "ghost" | "outline";
  multiple?: boolean;
  accept?: string;
  fileName?: string;
  maxFileSizeMb?: number;
  maxFiles?: number;
  currentFilesLength?: number;
  buttonIcon?: React.ReactNode;
  buttonText?: string;
  horizontalAlignment?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}

const ContentButton = ({
  buttonIcon = <FaPaperclip color="inherit" fontSize={20} />,
  buttonText = "Anexar Arquivos",
}: Pick<ButtonAnexarArquivosProps, "buttonIcon" | "buttonText">) => (
  <Flex flexDir="row" gridGap={4} alignItems="center" color="inherit">
    {buttonIcon}
    {buttonText}
  </Flex>
);

function ButtonAnexarArquivos({
  onAnexar,
  variant = "outline",
  multiple = true,
  accept = "*",
  fileName,
  maxFileSizeMb,
  maxFiles,
  currentFilesLength = 0,
  buttonIcon,
  buttonText,
  horizontalAlignment,
  disabled,
  isLoading,
}: ButtonAnexarArquivosProps) {
  const [key, setKey] = useState(new Date().getTime());
  const inputRef = useRef<HTMLInputElement | null>(null);

  const checkMaxFileSize = (files: File[]) => {
    let hasFileAboveSizeLimit = false;
    if (maxFileSizeMb && maxFileSizeMb > 0) {
      hasFileAboveSizeLimit = files.some(
        (file) => file.size > maxFileSizeMb * 10 ** 6
      );
    }

    if (hasFileAboveSizeLimit) {
      showError(`O tamanho máximo para cada arquivo é de ${maxFileSizeMb} Mb`);
    }

    return hasFileAboveSizeLimit;
  };

  const checkMaxFiles = (files: File[]) => {
    let isAboveLimit = false;
    if (maxFiles) {
      isAboveLimit = files.length + currentFilesLength > maxFiles;
    }

    if (isAboveLimit) {
      showError(
        `Você pode selecionar no máximo ${maxFiles} arquivo${
          maxFiles === 1 ? "s" : ""
        }`
      );
    }

    return isAboveLimit;
  };

  const isAtFileLimit = maxFiles ? currentFilesLength >= maxFiles : false;

  return (
    <Flex
      flexDir={horizontalAlignment ? "row" : "column"}
      alignItems={horizontalAlignment ? "center" : undefined}
      gridGap={horizontalAlignment ? 4 : undefined}
      flexWrap="wrap"
    >
      <Box as="span" display="none" maxW="100%">
        <input
          multiple={multiple}
          key={key}
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={(ev) => {
            const files = ev.target.files;

            if (files) {
              let _files: File[] = [];

              for (let i = 0; i < files?.length; i++) {
                _files.push(files[i]);
              }

              const hasFileAboveSizeLimit = checkMaxFileSize(_files);

              const isAboveFileLimit = checkMaxFiles(_files);

              if (!hasFileAboveSizeLimit && !isAboveFileLimit) {
                onAnexar(_files);
              }

              setKey(new Date().getTime());
            }
          }}
        />
      </Box>

      {variant === "outline" ? (
        <OutlineButton
          onClick={() => inputRef?.current?.click()}
          disabled={isAtFileLimit || disabled}
          isLoading={isLoading}
        >
          {isAtFileLimit ? (
            "Número máximo de arquivos anexados"
          ) : (
            <ContentButton buttonIcon={buttonIcon} buttonText={buttonText} />
          )}
        </OutlineButton>
      ) : (
        <Button
          border="none"
          bg="transparent"
          color={colors.primary}
          onClick={() => inputRef?.current?.click()}
          disabled={isAtFileLimit || disabled}
          isLoading={isLoading}
        >
          {isAtFileLimit ? (
            "Número máximo de arquivos anexados"
          ) : (
            <ContentButton buttonIcon={buttonIcon} buttonText={buttonText} />
          )}
        </Button>
      )}

      {fileName ? (
        <Flex mt={horizontalAlignment ? 0 : 2} alignItems="center" gridGap={1}>
          <AiOutlineFile color={colors.primary} size={20} />
          <Text color={colors.primary} fontSize={16} wordBreak="break-word">
            {fileName}
          </Text>
        </Flex>
      ) : null}
    </Flex>
  );
}

export default ButtonAnexarArquivos;
